import { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import {
  IconButton,
  Tooltip,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  useTheme,
} from '@mui/material';
import { useTranslate } from 'react-admin';

interface VariableInterpolationHelpProps {
  size?: 'small' | 'medium';
}

/* eslint-disable no-template-curly-in-string */
const SYNTAX_BASIC = '${namespace:property}';
const SYNTAX_WITH_FILTERS = '${namespace:property|filter|...}';
const EXAMPLE_CALLER = '${call:caller_id_number}';
const EXAMPLE_URI_ENCODE = '${call:caller_id_number|uri_encode}';
const EXAMPLE_OREMPTY = '${custom_vars:my_var|orempty}';
/* eslint-enable no-template-curly-in-string */

const CodeBlock = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const theme = useTheme();
  return (
    <Box
      component='code'
      sx={{
        display: 'block',
        backgroundColor: theme.palette.action.hover,
        p: 1.5,
        borderRadius: 1,
        fontFamily: 'monospace',
        overflowX: 'auto',
      }}
    >
      {children}
    </Box>
  );
};

const VariableInterpolationHelp = ({ size = 'small' }: VariableInterpolationHelpProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const translate = useTranslate();

  const handleOpen = (): void => {
    setOpen(true);
  };
  const handleClose = (): void => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={translate('callflows.components.variableInterpolation.title')}>
        <IconButton size={size} sx={{ ml: 0.5 }} onClick={handleOpen}>
          <HelpOutlineRoundedIcon fontSize={size} />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {translate('callflows.components.variableInterpolation.title')}
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant='body1'>{translate('callflows.components.variableInterpolation.description')}</Typography>

          <Typography variant='h6' gutterBottom>
            {translate('callflows.components.variableInterpolation.syntax')}
          </Typography>
          <CodeBlock>
            {SYNTAX_BASIC}
            <br />
            {SYNTAX_WITH_FILTERS}
          </CodeBlock>

          <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
            {translate('callflows.components.variableInterpolation.namespaces')}
          </Typography>
          <Paper variant='outlined' sx={{ mb: 2 }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Namespace</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {translate('callflows.components.variableInterpolation.availableVariables')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>call:</code>
                  </TableCell>
                  <TableCell>{translate('callflows.components.variableInterpolation.namespaceCall')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>custom_vars:</code>
                  </TableCell>
                  <TableCell>{translate('callflows.components.variableInterpolation.namespaceCustomVars')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>sip_headers:</code>
                  </TableCell>
                  <TableCell>{translate('callflows.components.variableInterpolation.namespaceSipHeaders')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>root:</code>
                  </TableCell>
                  <TableCell>{translate('callflows.components.variableInterpolation.namespaceRoot')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>helpers:</code>
                  </TableCell>
                  <TableCell>{translate('callflows.components.variableInterpolation.namespaceHelpers')}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>

          <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
            {translate('callflows.components.variableInterpolation.filters')}
          </Typography>
          <Paper variant='outlined' sx={{ mb: 2 }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Filter</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{translate('callflows.interface.description')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>user_part</code>
                  </TableCell>
                  <TableCell>{translate('callflows.components.variableInterpolation.filterUserPart')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>domain_part</code>
                  </TableCell>
                  <TableCell>{translate('callflows.components.variableInterpolation.filterDomainPart')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>uri_encode</code>
                  </TableCell>
                  <TableCell>{translate('callflows.components.variableInterpolation.filterUriEncode')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>orempty</code>
                  </TableCell>
                  <TableCell>{translate('callflows.components.variableInterpolation.filterOrempty')}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>

          <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
            {translate('callflows.components.variableInterpolation.examples')}
          </Typography>
          <CodeBlock>
            {EXAMPLE_CALLER}
            <br />
            {EXAMPLE_URI_ENCODE}
            <br />
            {EXAMPLE_OREMPTY}
          </CodeBlock>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VariableInterpolationHelp;
